/**
 * One living continuity example — shown across Canonicalls until a user preserves their own source.
 */
import type { CanonicallsContinuityCarry, CarriedRepresentation } from "./canonicallsContinuityCarry";
import { saveContinuityCarry, loadContinuityCarry, coalesceContinuityCarry } from "./canonicallsContinuityCarry";
import type { PreservedCrossPost } from "./verifiedCrossPost";
import { loadLocalCrossPost, saveLocalCrossPost } from "./verifiedCrossPost";

export const LIVING_EXAMPLE_POST_ID = "cc-living-continuity" as const;

export const LIVING_EXAMPLE_DIGEST =
  "c0ffeec0ffeec0ffeec0ffeec0ffeec0ffeec0ffeec0ffeec0ffeec0ffeec0ff" as const;

export const LIVING_EXAMPLE_PRESERVED_AT = "2026-03-15T14:30:00.000Z" as const;

export const LIVING_EXAMPLE = {
  postId: LIVING_EXAMPLE_POST_ID,
  sourceTitle: "Canonical continuity registry note",
  actor: "Morgan Ellison",
  actorContinuity: "Morgan Ellison · continuity author",
  body: "We are preserving a canonical continuity source for future representations.",
  digest: LIVING_EXAMPLE_DIGEST,
  preservedAt: LIVING_EXAMPLE_PRESERVED_AT,
  agreementRef: "Signed export · obligation ICC-2026-04",
  destination: "Verified destination · continuity registry",
  institutionalNote: "Institutional continuity — obligation linked to the same canonical source.",
} as const;

export type LivingDriftExample = {
  surface: string;
  driftKind: "summarized" | "derived-mirror" | "representation";
  excerpt: string;
  note: string;
};

export const LIVING_REPRESENTATION_DRIFT: readonly LivingDriftExample[] = [
  {
    surface: "AI summary",
    driftKind: "summarized",
    excerpt: "Preserving a canonical source for future posts and exports.",
    note: "Wording shortened — lineage returns to source.",
  },
  {
    surface: "Screenshot capture",
    driftKind: "representation",
    excerpt: "Registry note (image) — caption references an older title.",
    note: "Visual copy differs — source text unchanged.",
  },
  {
    surface: "Website mirror",
    driftKind: "derived-mirror",
    excerpt: "Future representations — canonical continuity registry.",
    note: "Headline reordered — canonical record remains inspectable.",
  },
] as const;

const LIVING_REPRESENTATIONS: CarriedRepresentation[] = [
  { surface: "Canonical source", drift: "canonical" },
  { surface: "Social post", drift: "representation" },
  { surface: "Professional network", drift: "representation" },
  { surface: "Website mirror", drift: "derived-mirror" },
  { surface: "AI summary", drift: "summarized" },
  { surface: "Signed export", drift: "exported" },
  { surface: "Agreement reference", drift: "agreement-linked" },
];

export function buildLivingContinuityCarry(
  lineageState: CanonicallsContinuityCarry["lineageState"] = "active",
): CanonicallsContinuityCarry {
  return {
    version: 1,
    postId: LIVING_EXAMPLE.postId,
    sourceTitle: LIVING_EXAMPLE.sourceTitle,
    actor: LIVING_EXAMPLE.actorContinuity,
    digest: LIVING_EXAMPLE.digest,
    lineageState,
    verificationState: "local-snapshot",
    representationCount: LIVING_REPRESENTATIONS.length - 1,
    representations: LIVING_REPRESENTATIONS,
    agreementLinked: true,
    lastTraversalStep: "origin",
    updatedAt: LIVING_EXAMPLE_PRESERVED_AT,
    evidence: [],
  };
}

export function buildLivingPreservedCrossPost(): PreservedCrossPost {
  return {
    postId: LIVING_EXAMPLE.postId,
    digest: LIVING_EXAMPLE.digest,
    artifactUrl: "",
    verifyUrl: "",
    publicContinuityUrl: `/canonicalize?post=${encodeURIComponent(LIVING_EXAMPLE.postId)}`,
    replayUrl: `/replay?cross_post=${encodeURIComponent(LIVING_EXAMPLE.postId)}`,
    preservedAt: LIVING_EXAMPLE.preservedAt,
    draft: {
      title: LIVING_EXAMPLE.sourceTitle,
      body: LIVING_EXAMPLE.body,
      author: LIVING_EXAMPLE.actorContinuity,
      aiAssisted: false,
      humanConfirmed: true,
    },
    mode: "local",
    ledgerEventCount: 0,
  };
}

export function hasLocalEvidence(carry: CanonicallsContinuityCarry): boolean {
  return Boolean(carry.evidence?.length);
}

export function isUserOwnedCarry(carry: CanonicallsContinuityCarry): boolean {
  return Boolean(carry.postId && carry.postId !== LIVING_EXAMPLE_POST_ID);
}

export type LivingExampleOptions = {
  disableLivingExample?: boolean;
};

export function seedLivingContinuityExampleIfEmpty(options?: LivingExampleOptions): void {
  if (options?.disableLivingExample) return;
  const current = loadContinuityCarry();
  if (isUserOwnedCarry(current)) return;
  const record = buildLivingPreservedCrossPost();
  saveLocalCrossPost(record);
  saveContinuityCarry(buildLivingContinuityCarry(current.lineageState === "dormant" ? "active" : current.lineageState));
}

export function resolveEffectiveCarry(
  carry: CanonicallsContinuityCarry,
  options?: LivingExampleOptions,
): {
  carry: CanonicallsContinuityCarry;
  isLivingExample: boolean;
} {
  if (options?.disableLivingExample || isUserOwnedCarry(carry) || hasLocalEvidence(carry)) {
    return { carry: coalesceContinuityCarry(carry), isLivingExample: false };
  }
  const living = buildLivingContinuityCarry(carry.lineageState);
  return {
    carry: {
      ...living,
      lineageState: carry.lineageState,
      agreementLinked: carry.agreementLinked || living.agreementLinked,
      lastTraversalStep: carry.lastTraversalStep ?? living.lastTraversalStep,
      verificationState:
        carry.verificationState !== "none" ? carry.verificationState : living.verificationState,
      digest: carry.digest ?? living.digest,
    },
    isLivingExample: true,
  };
}

export function getLivingRecordIfEffective(
  postId: string | null | undefined,
  options?: LivingExampleOptions,
): PreservedCrossPost | null {
  if (options?.disableLivingExample) {
    if (!postId) return null;
    return loadLocalCrossPost(postId);
  }
  if (!postId) return buildLivingPreservedCrossPost();
  if (postId === LIVING_EXAMPLE_POST_ID) return loadLocalCrossPost(postId) ?? buildLivingPreservedCrossPost();
  return loadLocalCrossPost(postId);
}
